import { PropsWithChildren } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, defineMessage, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { useLocalizedPartnerdirectory } from "@/libraries/web-framework/src/localization/use-localized-partnerdirectory";
import {
    TypographyStyles,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import { CardIcon, TitleCard } from "./components/card";
import { MotionContainer } from "./components/motion-container";
import { RowGrid } from "./layouts/row-grid";
import { LinkColumn } from "./layouts/subnav-link-columns";
import { useGlobalNavContext, DivOnHoverExit } from "./nav-links-container";

type ResourceData = {
    id: string;
    title: ReturnType<typeof defineMessage>;
    content: ReturnType<typeof defineMessage>;
    href: string;
    icon?: CardIcon;
};

export function useResourcesData(): Array<ResourceData> {
    return [
        {
            id: "resources.customer-support",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.customer-support.title",
                defaultMessage: "Customer Support"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.customer-support.content",
                defaultMessage: "Ask questions, report bugs & give us feedback"
            }),
            href: "//support.atlassian.com/"
        },
        {
            id: "resources.find-partners",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.find-partners.title",
                defaultMessage: "Find Partners"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.find-partners.content",
                defaultMessage:
                    "Consulting, training & product customization support"
            }),
            href: useLocalizedPartnerdirectory()
        },
        {
            id: "resources.migration-program",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.migration-program.title",
                defaultMessage: "Atlassian Ascend"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.migration-program.content",
                defaultMessage: "Resources and support for your transformation"
            }),
            href: useLocalizedHref("/migration")
        },
        {
            id: "resources.community",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.community.title",
                defaultMessage: "Community"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.community.content",
                defaultMessage:
                    "Learn, connect and grow with the Atlassian Community"
            }),
            href: "//community.atlassian.com/"
        }
    ];
}

export function useResourcesLinks() {
    return {
        support: {
            id: "support",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.links.support",
                defaultMessage: "Support"
            }),
            items: [
                {
                    id: "general-inquiries",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.general-inquiries",
                        defaultMessage: "General Inquiries"
                    }),
                    href: useLocalizedHref("/company/contact/general-inquiries")
                },
                {
                    id: "technical-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.technical-support",
                        defaultMessage: "Technical Support"
                    }),
                    href: "//support.atlassian.com/contact/"
                },
                {
                    id: "product-advice",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.product-advice",
                        defaultMessage: "Product Advice"
                    }),
                    href: useLocalizedHref(
                        "/company/contact/product-evaluator-advice"
                    )
                },
                {
                    id: "pricing-billing",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.pricing-billing",
                        defaultMessage: "Pricing and Billing"
                    }),
                    href: useLocalizedHref(
                        "/company/contact/purchasing-licensing"
                    )
                },
                {
                    id: "partner-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.partner-support",
                        defaultMessage: "Partner Support"
                    }),
                    href: useLocalizedHref("/partners")
                },
                {
                    id: "developer-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.developer-support",
                        defaultMessage: "Developer Support"
                    }),
                    href: "//developer.atlassian.com/"
                },
                {
                    id: "enterprise-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.enterprise-support",
                        defaultMessage: "Enterprise Support"
                    }),
                    href: useLocalizedHref("/enterprise/services")
                },
                {
                    id: "purchasing-licensing",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.purchasing-billing",
                        defaultMessage: "Purchasing and Licensing"
                    }),
                    href: useLocalizedHref("/licensing/purchase-licensing")
                }
            ]
        },
        learn: {
            id: "resources",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.links.resources",
                defaultMessage: "Resources"
            }),
            items: [
                {
                    id: "project-management",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.project-management",
                        defaultMessage: "Project Management"
                    }),
                    href: useLocalizedHref("/project-management")
                },
                {
                    id: "project-collaboration",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.project-collaboration",
                        defaultMessage: "Project Collaboration"
                    }),
                    href: useLocalizedHref(
                        "/work-management/project-collaboration"
                    )
                },
                {
                    id: "agile",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.agile",
                        defaultMessage: "Agile"
                    }),
                    href: useLocalizedHref("/agile")
                },
                {
                    id: "team-playbook",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.team-playbook",
                        defaultMessage: "Team Playbook"
                    }),
                    href: useLocalizedHref("/team-playbook")
                },
                {
                    id: "learning",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.learning",
                        defaultMessage: "Atlassian Learning"
                    }),
                    href: "//community.atlassian.com/learning"
                },
                {
                    id: "product-documentation",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.product-documentation",
                        defaultMessage: "Product Documentation"
                    }),
                    href: "//confluence.atlassian.com/display/ALLDOC/Atlassian+Documentation"
                },
                {
                    id: "get-started",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.get-started",
                        defaultMessage: "Get Started"
                    }),
                    href: useLocalizedHref("/get-started")
                }
            ]
        }
    } as const;
}

const ResourcesGrid = ({ children }: PropsWithChildren) => {
    return (
        <RowGrid
            numCols={4}
            rowGap={token("space.500")}
            columnGap={token("space.400")}
            paddingBlock={token("space.500")}
            paddingInline={token("space.500")}
        >
            {children}
        </RowGrid>
    );
};

export const ResourcesSubnav = ({
    productKey = "home"
}: {
    productKey?: ProductKeyTypes;
}) => {
    const intl = useIntl();

    const resourcesData = useResourcesData();
    const resourcesLinks = useResourcesLinks();
    const linkColumns = Object.values(resourcesLinks);

    const { direction } = useGlobalNavContext();

    return (
        <DivOnHoverExit>
            <TypographyStyles>
                <MotionContainer direction={direction}>
                    <ResourcesGrid>
                        {resourcesData.map(
                            ({ id, title, content, ...rest }) => (
                                <TitleCard
                                    key={id}
                                    title={intl.formatMessage(title)}
                                    content={intl.formatMessage(content)}
                                    productKey={productKey}
                                    {...rest}
                                />
                            )
                        )}
                    </ResourcesGrid>
                    <div
                        css={css({
                            backgroundColor: wacToken("color.neutral.light100")
                        })}
                    >
                        <RowGrid
                            numCols={2}
                            columnGap={token("space.400")}
                            paddingBlock={token("space.500")}
                            paddingInline={token("space.500")}
                        >
                            {linkColumns.map((column) => {
                                return (
                                    <LinkColumn key={column.id}>
                                        <h2 className="nav-column-header">
                                            <FormattedMessage
                                                {...column.title}
                                            />
                                        </h2>
                                        <ul
                                            css={css({
                                                listStyleType: "none",
                                                margin: "0",
                                                padding: "5px 0 0 0"
                                            })}
                                        >
                                            <RowGrid
                                                numCols={2}
                                                columnGap={token("space.400")}
                                                numRows={4}
                                                rowHeight={token("space.500")}
                                            >
                                                {column.items.map((link) => {
                                                    return (
                                                        <li
                                                            key={link.id}
                                                            css={css({
                                                                listStyleType:
                                                                    "none",
                                                                margin: "0",
                                                                padding: "8px 0"
                                                            })}
                                                        >
                                                            <div
                                                                key={link.id}
                                                                css={css({
                                                                    // nav doesn't follow the typography
                                                                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                                                    a: {
                                                                        fontWeight: 400,
                                                                        fontSize:
                                                                            "0.875rem",
                                                                        lineHeight:
                                                                            "1.5rem",
                                                                        color: wacToken(
                                                                            "color.primary.black"
                                                                        )
                                                                    },

                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "end"
                                                                })}
                                                            >
                                                                <Link
                                                                    type="anchor"
                                                                    href={
                                                                        link.href
                                                                    }
                                                                    text={intl.formatMessage(
                                                                        link.text
                                                                    )}
                                                                    ariaLabel={intl.formatMessage(
                                                                        link.text
                                                                    )}
                                                                    dataEventProps={{
                                                                        "data-event":
                                                                            "clicked",
                                                                        "data-event-component":
                                                                            "linkButton",
                                                                        "data-event-container":
                                                                            "globalNavSubnav",
                                                                        "data-product-key":
                                                                            productKey
                                                                    }}
                                                                />
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </RowGrid>
                                        </ul>
                                    </LinkColumn>
                                );
                            })}
                        </RowGrid>
                    </div>
                </MotionContainer>
            </TypographyStyles>
        </DivOnHoverExit>
    );
};
